import { useCallback, useState, type Dispatch } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { ErrorState } from '@/components/ErrorState';
import { LinkMascot } from '@/components/LinkMascot';
import { useDeclaredExternalAccounts } from '@/hooks/queries/useDeclaredExternalAccounts';
import { useStreamCandidates } from '@/hooks/queries/useStreamCandidates';
import { useAuthedFetcher } from '@/hooks/queries/swrConfig';
import { ApiError, classificationApi, externalAccountsApi, semanticClassApi } from '@/lib/api';
import type { SemanticClassWire, StreamCandidate } from '@/types/onboarding';
import { deferredCount, type StreamsAction, type StreamsState } from '@/components/classification/streams.helpers';
import { StreamCard } from '@/components/classification/StreamCard';
import { TotalsCheckCard } from '@/components/classification/TotalsCheckCard';

export interface StepStreamsProps {
  state: StreamsState;
  dispatch: Dispatch<StreamsAction>;
  onBack: () => void;
  onContinue: () => void;
  onSkip: () => void;
}

export function StepStreams({ state, dispatch, onBack, onContinue, onSkip }: StepStreamsProps) {
  const authedFetch = useAuthedFetcher();
  // The streams_loaded dispatch lives in OnboardingFlow (the state must populate
  // even when the user resumes past this step); this hook call dedupes onto the
  // same SWR key and serves the totals + error surface.
  const { data: candidates, error, isLoading, mutate: refreshCandidates } = useStreamCandidates();
  const { data: declaredAccounts, mutate: refreshDeclared } = useDeclaredExternalAccounts();
  const [claimedFromSearch, setClaimedFromSearch] = useState(0);

  const postConfirmation = useCallback(
    async (
      stream: StreamCandidate,
      cls: SemanticClassWire,
      declaredExternalAccountId: string | null,
    ) => {
      dispatch({ type: 'posting', streamKey: stream.streamKey });
      const excluded = state.excluded[stream.streamKey] ?? [];
      try {
        const response = await authedFetch((token) =>
          classificationApi.confirmStream(token, {
            streamKey: stream.streamKey,
            class: cls,
            selectedTransactionIds: stream.transactions
              .filter((tx) => !excluded.includes(tx.id))
              .map((tx) => tx.id),
            deselectedTransactionIds: excluded,
            declaredExternalAccountId,
          }),
        );
        dispatch({
          type: 'answered',
          streamKey: stream.streamKey,
          answer: {
            kind: 'classified',
            class: cls,
            semanticUpdatedCount: response.semanticUpdatedCount,
            deferredToReview: response.deferredToReview,
          },
        });
        // The card snapshot keeps itself on screen; the refetch updates the
        // class totals the verify check reads.
        void refreshCandidates();
      } catch (err) {
        dispatch({ type: 'post_failed', streamKey: stream.streamKey });
        if (err instanceof ApiError && err.status === 404) {
          toast.error('Esse grupo mudou desde que a tela carregou. Atualize a página para continuar.');
          return;
        }
        toast.error(err instanceof ApiError ? err.message : 'Não foi possível salvar a resposta.');
      }
    },
    [authedFetch, dispatch, refreshCandidates, state.excluded],
  );

  const handlePick = useCallback(
    (stream: StreamCandidate, cls: SemanticClassWire) => {
      // The perimeter follow-up: income from a payer that IS the user implies an
      // account outside the connected perimeter — ask which before writing.
      if (cls === 'income' && stream.why.payerIsSelf) {
        dispatch({ type: 'open_income_follow_up', streamKey: stream.streamKey });
        return;
      }
      void postConfirmation(stream, cls, null);
    },
    [dispatch, postConfirmation],
  );

  const handleConfirmIncome = useCallback(
    async (
      stream: StreamCandidate,
      source: { declaredExternalAccountId?: string; newAccountName?: string },
    ) => {
      const newAccountName = source.newAccountName;
      if (newAccountName) {
        dispatch({ type: 'posting', streamKey: stream.streamKey });
        try {
          const account = await authedFetch((token) =>
            externalAccountsApi.declare(token, newAccountName),
          );
          void refreshDeclared();
          await postConfirmation(stream, 'income', account.id);
        } catch (err) {
          dispatch({ type: 'post_failed', streamKey: stream.streamKey });
          toast.error(err instanceof ApiError ? err.message : 'Não foi possível registrar a conta.');
        }
        return;
      }
      void postConfirmation(stream, 'income', source.declaredExternalAccountId ?? null);
    },
    [authedFetch, dispatch, postConfirmation, refreshDeclared],
  );

  const handleDefer = useCallback(
    (stream: StreamCandidate) => {
      // Defer writes nothing server-side — the stream simply remains a candidate
      // and the in-app review re-asks.
      dispatch({ type: 'answered', streamKey: stream.streamKey, answer: { kind: 'deferred' } });
    },
    [dispatch],
  );

  const handleCancelFollowUp = useCallback(
    () => dispatch({ type: 'cancel_income_follow_up' }),
    [dispatch],
  );
  const handleToggleTxList = useCallback(
    (streamKey: string) => dispatch({ type: 'toggle_tx_list', streamKey }),
    [dispatch],
  );
  const handleToggleWhy = useCallback(
    (streamKey: string) => dispatch({ type: 'toggle_why', streamKey }),
    [dispatch],
  );
  const handleToggleTransaction = useCallback(
    (streamKey: string, transactionId: string) =>
      dispatch({ type: 'toggle_transaction', streamKey, transactionId }),
    [dispatch],
  );
  const handleSelectAll = useCallback(
    (streamKey: string) => dispatch({ type: 'select_all', streamKey }),
    [dispatch],
  );
  const handleClearSelection = useCallback(
    (streamKey: string) => dispatch({ type: 'clear_selection', streamKey }),
    [dispatch],
  );
  const handleCheckTotal = useCallback(
    (cls: SemanticClassWire) => dispatch({ type: 'check_total', class: cls }),
    [dispatch],
  );
  const handleOpenMissing = useCallback(
    (cls: SemanticClassWire) => dispatch({ type: 'open_missing', class: cls }),
    [dispatch],
  );
  const handleCloseMissing = useCallback(() => dispatch({ type: 'close_missing' }), [dispatch]);

  // A claim from the "Falta algo" panel is the same answer as picking the option
  // on the card — routed through handlePick so self-paid income streams get the
  // perimeter follow-up (which then shows on the card above), never a silent
  // null-declared write.
  const handleClaimStream = useCallback(
    (stream: StreamCandidate, cls: SemanticClassWire) => {
      dispatch({ type: 'close_missing' });
      handlePick(stream, cls);
    },
    [dispatch, handlePick],
  );

  const handleClaimedTransaction = useCallback(() => {
    setClaimedFromSearch((current) => current + 1);
    void refreshCandidates();
  }, [refreshCandidates]);

  // "Isso não está certo aqui": removing a counted row is a semantic re-decision —
  // an inflow that isn't renda is neutral; an outflow that isn't fatura/investimento
  // is an ordinary spend. The refetch drops the row from the total.
  const writeSemanticClass = useCallback(
    async (transactionId: string, cls: SemanticClassWire) => {
      await authedFetch((token) => semanticClassApi.setBatch(token, [transactionId], cls));
      void refreshCandidates();
    },
    [authedFetch, refreshCandidates],
  );

  // One tap on the row's × writes server-side with no confirmation step, and the
  // only other way back is finding the row again through the search box — so the
  // undo has to be offered at the moment of the write.
  const handleDeselectCounted = useCallback(
    async (transactionId: string, cls: SemanticClassWire) => {
      const handleUndo = () => {
        void writeSemanticClass(transactionId, cls).catch((err: unknown) => {
          toast.error(err instanceof ApiError ? err.message : 'Não foi possível desfazer.');
        });
      };
      try {
        await writeSemanticClass(transactionId, cls === 'income' ? 'neutral' : 'spend');
        toast.success('Lançamento removido deste total.', {
          action: { label: 'Desfazer', onClick: handleUndo },
        });
      } catch (err) {
        toast.error(err instanceof ApiError ? err.message : 'Não foi possível remover o lançamento.');
      }
    },
    [writeSemanticClass],
  );

  const handleRetryCandidates = useCallback(() => {
    void refreshCandidates();
  }, [refreshCandidates]);

  const loadFailed = Boolean(error) && state.streams.length === 0;
  const noStreams = !isLoading && !loadFailed && state.streams.length === 0;

  return (
    <section>
      <h1 className="text-[22px] font-bold tracking-tight">O que muda suas contas</h1>
      <div className="mt-2.5 flex items-start gap-3 rounded-2xl border border-border-brand bg-brand-50 px-3.5 py-3">
        <LinkMascot state="attention" size="xs" />
        <p className="min-w-0 flex-1 text-[13px]/relaxed text-pretty text-primary">
          O Link separou os grupos que pesam no seu mês. Cada resposta vale para o grupo
          inteiro, passado e futuro. Se preferir, deixe para depois: dá para responder tudo
          dentro do app.
        </p>
      </div>

      {isLoading && state.streams.length === 0 && (
        <div className="mt-4 flex flex-col gap-3">
          <Skeleton className="h-48 w-full rounded-3xl" />
          <Skeleton className="h-48 w-full rounded-3xl" />
        </div>
      )}

      {loadFailed && (
        <div className="mt-4">
          <ErrorState
            title="Erro ao carregar os grupos"
            message="Não conseguimos carregar os grupos para revisar agora. Verifique sua internet e tente novamente."
            onAction={handleRetryCandidates}
          />
        </div>
      )}

      {noStreams && (
        <div className="mt-4 grid min-h-24 place-items-center gap-1.5 rounded-2xl border border-dashed border-border-subtle bg-surface px-5 py-8">
          <p className="text-sm text-text-tertiary">Nenhum grupo para revisar por enquanto.</p>
          <p className="text-xs text-text-tertiary">
            Assim que a importação terminar, o Link separa os grupos que valem uma pergunta.
          </p>
        </div>
      )}

      <div className="mt-4 flex flex-col gap-3">
        {state.streams.map((stream, index) => (
          <StreamCard
            key={stream.streamKey}
            stream={stream}
            index={index}
            count={state.streams.length}
            answer={state.answers[stream.streamKey]}
            excludedIds={state.excluded[stream.streamKey] ?? []}
            txOpen={Boolean(state.txOpen[stream.streamKey])}
            whyOpen={Boolean(state.whyOpen[stream.streamKey])}
            followUpOpen={state.incomeFollowUpFor === stream.streamKey}
            posting={state.posting.includes(stream.streamKey)}
            declaredAccounts={declaredAccounts ?? []}
            onToggleTxList={handleToggleTxList}
            onToggleWhy={handleToggleWhy}
            onToggleTransaction={handleToggleTransaction}
            onSelectAll={handleSelectAll}
            onClearSelection={handleClearSelection}
            onPick={handlePick}
            onDefer={handleDefer}
            onConfirmIncome={handleConfirmIncome}
            onCancelFollowUp={handleCancelFollowUp}
          />
        ))}
      </div>

      {!noStreams && !loadFailed && (
        <div className="mt-4">
          <TotalsCheckCard
            totals={candidates?.totals}
            reachedRows={candidates?.reachedRows ?? 0}
            state={state}
            deferredCount={deferredCount(state)}
            declaredCount={claimedFromSearch}
            onCheckTotal={handleCheckTotal}
            onOpenMissing={handleOpenMissing}
            onCloseMissing={handleCloseMissing}
            onClaimStream={handleClaimStream}
            onClaimedTransaction={handleClaimedTransaction}
            onDeselectCounted={handleDeselectCounted}
          />
        </div>
      )}

      <div className="mt-6 flex flex-col-reverse gap-3 border-t border-border-subtle pt-4 sm:flex-row sm:items-center sm:justify-between">
        <Button variant="ghost" onClick={onBack}>
          Voltar
        </Button>
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:gap-3">
          <Button variant="link" onClick={onSkip}>
            Fazer depois no app
          </Button>
          <Button onClick={onContinue}>Continuar</Button>
        </div>
      </div>
    </section>
  );
}
